import { useRef, useState, useEffect } from 'react'
import { useSearchParams } from 'react-router'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import GetApp from '@mui/icons-material/GetApp'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import { styled, alpha } from '@mui/material/styles'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { addMinutes } from 'date-fns'

import {
    addMonthUTC,
    dateToYearMonthDayString,
    dateToYearMonthString,
    endOfMonthUTC,
    startOfMonthUTC,
} from 'common/api/v1/helpers'
import type { ListResult } from 'common/api/v1/types'
import { findLicenseTypeById } from 'common-billing-server/index'
import type { AiUsageSummary, Billing2PeriodWithDetails, Customer } from 'common-billing-server/types'

import { useBillingApi } from '../../api/billing/billing-context'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { Column, RefreshableDataFetchingPaginatedTable } from '../common/Table'
import { AutoComplete } from '../common/AutoComplete'
import { parseISOUTC } from '../../format-date'
import Wrapper from '../common/Wrapper'
import { AiUsage } from './AiUsage'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
const SummaryGrid = styled('dl')(({ theme }) => ({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridTemplateRows: 'repeat(auto-fill, auto)',
    dd: { marginLeft: 30, marginTop: 5 },
}))

const ServiceGrid = styled('dl')(() => ({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridTemplateRows: 'repeat(auto-fill, auto)',
    alignItems: 'center',
    dd: { margin: 0, marginLeft: 30 },
}))

// Define interfaces for minute-based billing data
interface ServiceTypeUsage {
    serviceType: string
    minutes: number
    bytes: bigint
    numServices: number
    rtuDeduction?: {
        allocatedRtus: number
        minutesCovered: number
        servicesFullyCovered: number
        rtusUsed?: number
        ratedMinutesCovered?: number
    }
    flowDeduction?: {
        flowLicensesUsed: number
        minutesCovered: number
        outputsFullyCovered: number
    }
    billableMinutes?: number
}

const columns: Column<Billing2PeriodWithDetails, ''>[] = [
    { title: 'Customer name', valueForColumn: (p) => p.customer?.name },
    { title: 'Period', valueForColumn: (p) => dateToYearMonthString(p.startAt) },
    { title: 'Latest report', valueForColumn: (p) => dateToYearMonthDayString(p.reportedToDate) },
    { title: 'License key', valueForColumn: (p) => p.license?.licenseKey },
    { title: 'License type', valueForColumn: (p) => findLicenseTypeById(p.license?.licenseType)?.name },
    { title: 'Host', valueForColumn: (p) => p.host },

    { title: 'Total services', valueForColumn: (p) => p.serviceTypes.reduce((sum, st) => sum + st.numServices, 0) },
    {
        title: 'Usage minutes',
        valueForColumn: (p) => p.totalUsageMinutes.toLocaleString(),
    },
    {
        title: 'Rated minutes',
        valueForColumn: (p) => {
            const rated = p.totalRatedMinutes
            return rated.toLocaleString()
        },
    },
    {
        title: 'RTU covered',
        valueForColumn: (p) => {
            // Leg-weighted periods anchor RTU coverage to rated minutes; older periods to usage.
            // A range can straddle the gate, so each row names its unit.
            const legWeighted = p.serviceTypes.some((st) => st.rtuDeduction?.rtusUsed !== undefined)
            const covered = p.serviceTypes.reduce(
                (sum, st) =>
                    sum +
                    (st.rtuDeduction ? (st.rtuDeduction.ratedMinutesCovered ?? st.rtuDeduction.minutesCovered) : 0),
                0
            )
            return covered > 0 ? `${covered.toLocaleString()} ${legWeighted ? 'rated' : 'usage'}` : '-'
        },
    },
    {
        title: 'Flow-covered rated',
        valueForColumn: (p) => {
            const covered = p.serviceTypes.reduce((sum, st) => sum + (st.flowDeduction?.minutesCovered || 0), 0)
            return covered > 0 ? covered.toLocaleString() : '-'
        },
    },
    {
        title: 'Billable minutes',
        valueForColumn: (p) => {
            const billable =
                p.totalBillableMinutes !== undefined
                    ? p.totalBillableMinutes
                    : p.serviceTypes.reduce(
                          (sum, st) => sum + (st.billableMinutes !== undefined ? st.billableMinutes : st.ratedMinutes),
                          0
                      )
            return billable.toLocaleString()
        },
    },
    { title: 'Total Data (GiB)', valueForColumn: (p) => bytesToGiB(p.totalBytes) },
]

// Component for the expandable row
const ExpandableRow = ({
    row,
    columns,
}: {
    row: Billing2PeriodWithDetails
    columns: Column<Billing2PeriodWithDetails, ''>[]
}) => {
    const [open, setOpen] = useState(false)

    const hasServiceTypeDetails = row.serviceTypes && row.serviceTypes.length > 0

    return (
        <>
            <TableRow
                hover
                sx={{ '& > .MuiTableCell-root': { borderBottom: 'unset' } }}
                onClick={() => {
                    if (!hasServiceTypeDetails) return
                    setOpen(!open)
                }}
            >
                <TableCell padding="checkbox">
                    {hasServiceTypeDetails && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation()
                                setOpen(!open)
                            }}
                        >
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    )}
                </TableCell>
                {columns.map((column, index) => (
                    <TableCell key={column.title}>{String(column.valueForColumn(row))}</TableCell>
                ))}
                <TableCell padding="checkbox">
                    <Link underline="none" href={`/api/billing2/period/${row.id}`} onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" title="Download PDF report">
                            <GetApp />
                        </IconButton>
                    </Link>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell
                    sx={{ py: 0, backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08) }}
                    colSpan={columns.length + 2}
                >
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom component="div">
                                Service Type Details
                            </Typography>
                            <Grid container spacing={2}>
                                {row.serviceTypes.map((service) => (
                                    <Grid key={service.serviceType}>
                                        <Paper elevation={1} sx={{ p: 2, backgroundColor: 'background.paper' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="subtitle1">
                                                    <Chip
                                                        label={service.serviceType}
                                                        color={
                                                            service.serviceType === 'Ingress'
                                                                ? 'primary'
                                                                : service.serviceType === 'Egress'
                                                                  ? 'secondary'
                                                                  : 'default'
                                                        }
                                                        size="small"
                                                    />
                                                </Typography>
                                                <Typography variant="caption">
                                                    {service.numServices} service{service.numServices !== 1 ? 's' : ''}
                                                </Typography>
                                            </Box>
                                            <ServiceGrid sx={{ mt: 1 }}>
                                                <dt>
                                                    <Typography sx={{ fontSize: '0.75rem' }}>Usage</Typography>
                                                </dt>
                                                <dd>
                                                    <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                                                        {service.usageMinutes.toLocaleString()} minutes
                                                    </Typography>
                                                </dd>
                                                <dt>
                                                    <Typography sx={{ fontSize: '0.75rem' }}>Rated</Typography>
                                                </dt>
                                                <dd>
                                                    <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                                                        {service.ratedMinutes.toLocaleString()} minutes
                                                    </Typography>
                                                </dd>
                                                {service.rtuDeduction &&
                                                    (service.rtuDeduction.minutesCovered > 0 ||
                                                        (service.rtuDeduction.rtusUsed ?? 0) > 0) && (
                                                        <>
                                                            <dt>
                                                                <Typography
                                                                    sx={{ color: 'success.main', fontSize: '0.75rem' }}
                                                                >
                                                                    RTU
                                                                </Typography>
                                                            </dt>
                                                            <dd>
                                                                <Typography
                                                                    component="span"
                                                                    sx={{ color: 'success.main', fontSize: '0.75rem' }}
                                                                >
                                                                    {service.rtuDeduction.allocatedRtus} allocated
                                                                    {service.rtuDeduction.rtusUsed !== undefined
                                                                        ? `, ${service.rtuDeduction.rtusUsed} used`
                                                                        : ''}
                                                                </Typography>
                                                            </dd>
                                                            <dt>
                                                                <Typography
                                                                    sx={{ color: 'success.main', fontSize: '0.75rem' }}
                                                                >
                                                                    RTU Covered
                                                                </Typography>
                                                            </dt>
                                                            <dd>
                                                                <Typography
                                                                    component="span"
                                                                    sx={{ color: 'success.main', fontSize: '0.75rem' }}
                                                                >
                                                                    {service.rtuDeduction.ratedMinutesCovered !==
                                                                    undefined
                                                                        ? `${service.rtuDeduction.ratedMinutesCovered.toLocaleString()} rated minutes`
                                                                        : `${service.rtuDeduction.minutesCovered.toLocaleString()} minutes`}
                                                                </Typography>
                                                            </dd>
                                                        </>
                                                    )}
                                                {service.flowDeduction && service.flowDeduction.minutesCovered > 0 && (
                                                    <>
                                                        <dt>
                                                            <Typography
                                                                sx={{ color: 'success.main', fontSize: '0.75rem' }}
                                                            >
                                                                Flow Covered (rated)
                                                            </Typography>
                                                        </dt>
                                                        <dd>
                                                            <Typography
                                                                component="span"
                                                                sx={{ color: 'success.main', fontSize: '0.75rem' }}
                                                            >
                                                                {service.flowDeduction.minutesCovered.toLocaleString()}{' '}
                                                                minutes ({service.flowDeduction.outputsFullyCovered}{' '}
                                                                fully covered outputs,{' '}
                                                                {service.flowDeduction.flowLicensesUsed} flows)
                                                            </Typography>
                                                        </dd>
                                                    </>
                                                )}
                                                <dt>
                                                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                                        Billable
                                                    </Typography>
                                                </dt>
                                                <dd>
                                                    <Typography
                                                        component="span"
                                                        sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                                                    >
                                                        {service.billableMinutes.toLocaleString()} minutes
                                                    </Typography>
                                                </dd>
                                                <dt>
                                                    <Typography sx={{ fontSize: '0.75rem' }}>Data</Typography>
                                                </dt>
                                                <dd>
                                                    <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                                                        {bytesToGiB(service.bytes)} GiB
                                                    </Typography>
                                                </dd>
                                            </ServiceGrid>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

export const Billing2Page = () => {
    const billingApi = useBillingApi()
    const tableRef = useRef<RefreshableDataFetchingPaginatedTable>(null)
    const [customer, setCustomer] = useState<Customer | null>(null)
    const [searchParams, setSearchParams] = useSearchParams()
    // ?? only replaces null, and the parameter is present but blank when no customer is selected.
    const customerId = searchParams.get('customerId') || undefined
    const startDateUrlParam =
        searchParams.get('startDate') ?? startOfMonthUTC(addMonthUTC(new Date(), -1)).toISOString()
    const endDateUrlParam = searchParams.get('endDate') ?? startOfMonthUTC(new Date()).toISOString()
    const { minDate, maxDate } = {
        minDate: addMonthUTC(new Date(endDateUrlParam), -12), // Allow selection up to 1 year back
        maxDate: endOfMonthUTC(new Date()), // Allow selection up to current month
    }
    const { addFailedOperation, errorBanner } = useFailedOperationBanner()
    const [serviceTypeSummary, setServiceTypeSummary] = useState<ServiceTypeUsage[]>([])
    const [totalUsageMinutes, setTotalUsageMinutes] = useState<number>(0)
    const [totalRatedMinutes, setTotalRatedMinutes] = useState<number>(0)
    const [totalBytes, setTotalBytes] = useState<bigint>(0n)
    const [totalServices, setTotalServices] = useState<number>(0)
    const [totalBillableMinutes, setTotalBillableMinutes] = useState<number>(0)
    const [billingPeriods, setBillingPeriods] = useState<Billing2PeriodWithDetails[]>([])
    const [aiUsage, setAiUsage] = useState<AiUsageSummary>()
    const [aiUsageError, setAiUsageError] = useState<Error>()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isLoadingAiUsage, setIsLoadingAiUsage] = useState(false)
    const isLoadingRef = useRef(false)

    useEffect(() => {
        const loadData = async () => {
            if (isLoadingRef.current) return

            isLoadingRef.current = true
            setIsLoading(true)

            try {
                await fetchBillings({ skip: 0, limit: 10000 })
            } catch (error) {
                // oxlint-disable-next-line no-console
                console.error('Error loading billing data:', error)
            } finally {
                setIsLoading(false)
                isLoadingRef.current = false
            }
        }

        void loadData()

        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [startDateUrlParam, endDateUrlParam, customerId])

    useEffect(() => {
        let cancelled = false

        const loadAiUsage = async () => {
            setIsLoadingAiUsage(true)
            setAiUsageError(undefined)
            try {
                const summary = await billingApi.getAiUsageSummary({
                    startDate: startDateUrlParam,
                    endDate: endDateUrlParam,
                    customerId,
                })
                if (!cancelled) setAiUsage(summary)
            } catch (error) {
                if (!cancelled) {
                    setAiUsage(undefined)
                    setAiUsageError(error instanceof Error ? error : new Error(String(error)))
                }
            } finally {
                if (!cancelled) setIsLoadingAiUsage(false)
            }
        }

        void loadAiUsage()
        return () => {
            cancelled = true
        }
    }, [billingApi, startDateUrlParam, endDateUrlParam, customerId])

    const setDate = (value: Date | null) => {
        if (!value || !value.getTime()) return

        // DatePicker returns local time, convert to UTC
        const utcDate = addMinutes(value, -value.getTimezoneOffset())

        // Set to start of selected month and start of next month in UTC
        // Backend uses exclusive end date (start_at < endDate), so we use first of next month
        searchParams.set('startDate', startOfMonthUTC(utcDate).toISOString())
        searchParams.set('endDate', startOfMonthUTC(addMonthUTC(utcDate, 1)).toISOString())
        setSearchParams(searchParams, { replace: true })
        tableRef.current?.refreshData()
    }

    const fetchBillings = async ({
        skip,
        limit,
    }: {
        skip?: number
        limit?: number
    }): Promise<ListResult<Billing2PeriodWithDetails>> => {
        try {
            const data = await billingApi.getBilling2(
                { skip, limit },
                { startDate: startDateUrlParam, endDate: endDateUrlParam, customerId }
            )

            // Store the summary data for display
            if (data.summary) {
                setServiceTypeSummary(data.summary.serviceTypes as ServiceTypeUsage[])
                const usageMinutes = data.summary.totalUsageMinutes!

                const ratedMinutes = data.summary.totalRatedMinutes!

                setTotalUsageMinutes(usageMinutes)
                setTotalRatedMinutes(ratedMinutes)
                setTotalBytes(data.summary.serviceTypes.reduce((acc, service) => acc + service.bytes, 0n))
                setTotalServices(
                    data.summary.serviceTypes.reduce((acc, service) => acc + (service as any).numServices || 0, 0)
                )
                setTotalBillableMinutes(
                    data.summary.totalBillableMinutes !== undefined
                        ? data.summary.totalBillableMinutes
                        : data.summary.serviceTypes.reduce(
                              (acc, service) =>
                                  acc +
                                  ((service as any).billableMinutes !== undefined
                                      ? (service as any).billableMinutes
                                      : service.ratedMinutes),
                              0
                          )
                )
            }

            // Store billing periods for the expandable table
            setBillingPeriods(data.items)

            return data
        } catch (error) {
            addFailedOperation({
                id: 'get-billing2',
                message: 'Failed fetching minute-based billing data',
            })
            throw error
        }
    }

    useEffect(() => {
        const loadCustomer = async () => {
            if (!customerId) {
                setCustomer(null)
                return
            }

            try {
                const result = await billingApi.listCustomers({
                    skip: 0,
                    limit: 1,
                    filter: { customerId },
                })
                if (result.items.length > 0) {
                    setCustomer(result.items[0])
                }
            } catch (error) {
                // oxlint-disable-next-line no-console
                console.error('Error loading customer from URL:', error)
            }
        }

        void loadCustomer()
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId])

    const changeCustomer = (c: Customer | null) => {
        setCustomer(c)
        c ? searchParams.set('customerId', c.id) : searchParams.delete('customerId')
        setSearchParams(searchParams, { replace: true })
        tableRef.current?.refreshData()
    }

    // One queried range can mix flat-rule and leg-weighted customers (the cut-over is per
    // customer), and the server merges both into one rtuDeduction per service type. minutesCovered
    // is usage-anchored under BOTH rules, so it is the one total that stays truthful for a mix;
    // the rated-anchored sum and RTU count exist only for the leg-weighted portion and render as
    // extra lines.
    const rtuCoveredUsageMinutes = serviceTypeSummary.reduce(
        (sum, service) => sum + (service.rtuDeduction?.minutesCovered ?? 0),
        0
    )
    const legRtuCoveredRatedMinutes = serviceTypeSummary.reduce(
        (sum, service) => sum + (service.rtuDeduction?.ratedMinutesCovered ?? 0),
        0
    )
    const totalRtusUsed = serviceTypeSummary.reduce((sum, service) => sum + (service.rtuDeduction?.rtusUsed ?? 0), 0)

    function makeBillingReportsCsvUrl(startDateString: string, endDateString: string) {
        const searchParams = new URLSearchParams()
        if (customerId) searchParams.set('customerId', customerId)
        searchParams.set('startDate', startDateString)
        searchParams.set('endDate', endDateString)
        searchParams.set('format', 'csv')
        searchParams.set('q', JSON.stringify({ skip: 0, limit: 10000 }))
        return `/api/billing2?${searchParams.toString()}`
    }

    return (
        <Wrapper>
            <Paper sx={{ padding: '10px' }}>
                <Grid container spacing={2} sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <AutoComplete<Customer>
                            placeholder={`Customer`}
                            value={customer}
                            isRequired
                            onValueSelected={(c) => changeCustomer(c)}
                            formatSelectedValue={(customer) => customer.name}
                            isClearable
                            api={(query) =>
                                billingApi.listCustomers({
                                    ...query,
                                    filter: { customerId, searchName: query.filter },
                                })
                            }
                            dataTestId={'billing-customer'}
                            margin="none"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 'auto' }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Month"
                                views={['year', 'month']}
                                format="MMMM yyyy"
                                value={parseISOUTC(startDateUrlParam)}
                                onChange={setDate}
                                minDate={minDate}
                                maxDate={maxDate}
                                disableFuture
                                slotProps={{
                                    textField: {
                                        sx: { width: { xs: '100%', sm: 300 } },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 'auto' }}>
                        <Link underline="none" href={makeBillingReportsCsvUrl(startDateUrlParam, endDateUrlParam)}>
                            <Button variant="outlined" startIcon={<GetApp />}>
                                Download minute usage CSV
                            </Button>
                        </Link>
                    </Grid>
                </Grid>
            </Paper>

            {serviceTypeSummary.length > 0 && (
                <Paper sx={{ mt: 2, padding: '20px' }}>
                    <Typography variant="h6" gutterBottom>
                        Summary
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <SummaryGrid>
                                <dt>
                                    <Typography>Total Services</Typography>
                                </dt>
                                <dd>
                                    <Typography component="span">{totalServices}</Typography>
                                </dd>
                                <dt>
                                    <Typography>Usage</Typography>
                                </dt>
                                <dd>
                                    <Typography component="span">
                                        {totalUsageMinutes.toLocaleString()} minutes
                                    </Typography>
                                </dd>
                                <dt>
                                    <Typography>Rated</Typography>
                                </dt>
                                <dd>
                                    <Typography component="span">
                                        {totalRatedMinutes.toLocaleString()} minutes
                                    </Typography>
                                </dd>
                                {rtuCoveredUsageMinutes > 0 && (
                                    <>
                                        <dt>
                                            <Typography sx={{ color: 'success.main' }}>RTU Covered</Typography>
                                        </dt>
                                        <dd>
                                            <Typography component="span" sx={{ color: 'success.main' }}>
                                                {rtuCoveredUsageMinutes.toLocaleString()} usage minutes
                                            </Typography>
                                        </dd>
                                    </>
                                )}
                                {legRtuCoveredRatedMinutes > 0 && (
                                    <>
                                        <dt>
                                            <Typography sx={{ color: 'success.main' }}>RTU Covered (rated)</Typography>
                                        </dt>
                                        <dd>
                                            <Typography component="span" sx={{ color: 'success.main' }}>
                                                {legRtuCoveredRatedMinutes.toLocaleString()} rated minutes
                                            </Typography>
                                        </dd>
                                    </>
                                )}
                                {totalRtusUsed > 0 && (
                                    <>
                                        <dt>
                                            <Typography sx={{ color: 'success.main' }}>RTUs Used</Typography>
                                        </dt>
                                        <dd>
                                            <Typography component="span" sx={{ color: 'success.main' }}>
                                                {totalRtusUsed.toLocaleString()}
                                            </Typography>
                                        </dd>
                                    </>
                                )}
                                {serviceTypeSummary.some((service) => service.flowDeduction?.minutesCovered) && (
                                    <>
                                        <dt>
                                            <Typography sx={{ color: 'success.main' }}>Flow Covered (rated)</Typography>
                                        </dt>
                                        <dd>
                                            <Typography component="span" sx={{ color: 'success.main' }}>
                                                {serviceTypeSummary
                                                    .reduce(
                                                        (sum, service) =>
                                                            sum + (service.flowDeduction?.minutesCovered || 0),
                                                        0
                                                    )
                                                    .toLocaleString()}{' '}
                                                rated minutes
                                            </Typography>
                                        </dd>
                                    </>
                                )}
                                <dt>
                                    <Typography sx={{ fontWeight: 'bold' }}>Billable</Typography>
                                </dt>
                                <dd>
                                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                                        {totalBillableMinutes.toLocaleString()} minutes
                                    </Typography>
                                </dd>
                                <dt>
                                    <Typography>Total Data</Typography>
                                </dt>
                                <dd>
                                    <Typography component="span">{bytesToGiB(totalBytes)} GiB</Typography>
                                </dd>
                            </SummaryGrid>
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Grid container spacing={2}>
                                {serviceTypeSummary.map((service) => (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={service.serviceType}>
                                        <Paper style={{ padding: '10px', backgroundColor: '#f5f5f5' }} elevation={1}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                {service.serviceType}
                                            </Typography>
                                            <ServiceGrid>
                                                <dt>
                                                    <Typography sx={{ fontSize: '0.75rem' }}>Services</Typography>
                                                </dt>
                                                <dd>
                                                    <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                                                        {service.numServices}
                                                    </Typography>
                                                </dd>
                                                <dt>
                                                    <Typography sx={{ fontSize: '0.75rem' }}>Usage</Typography>
                                                </dt>
                                                <dd>
                                                    <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                                                        {(
                                                            (service as any).usageMinutes || service.minutes
                                                        ).toLocaleString()}{' '}
                                                        minutes
                                                    </Typography>
                                                </dd>
                                                <dt>
                                                    <Typography sx={{ fontSize: '0.75rem' }}>Rated</Typography>
                                                </dt>
                                                <dd>
                                                    <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                                                        {(
                                                            (service as any).ratedMinutes ||
                                                            service.billableMinutes ||
                                                            service.minutes
                                                        ).toLocaleString()}{' '}
                                                        minutes
                                                    </Typography>
                                                </dd>
                                                {service.rtuDeduction &&
                                                    (service.rtuDeduction.minutesCovered > 0 ||
                                                        (service.rtuDeduction.rtusUsed ?? 0) > 0) && (
                                                        <>
                                                            <dt>
                                                                <Typography
                                                                    sx={{ color: 'success.main', fontSize: '0.75rem' }}
                                                                >
                                                                    RTU Covered
                                                                </Typography>
                                                            </dt>
                                                            <dd>
                                                                <Typography
                                                                    component="span"
                                                                    sx={{ color: 'success.main', fontSize: '0.75rem' }}
                                                                >
                                                                    {/* A merged summary entry can mix both rules; the
                                                                        usage-anchored total is truthful for both, the
                                                                        rated figure covers the leg-weighted portion. */}
                                                                    {`${service.rtuDeduction.minutesCovered.toLocaleString()} usage minutes`}
                                                                    {service.rtuDeduction.ratedMinutesCovered !==
                                                                    undefined
                                                                        ? ` (${service.rtuDeduction.ratedMinutesCovered.toLocaleString()} rated, ${service.rtuDeduction.rtusUsed} RTUs)`
                                                                        : ''}
                                                                </Typography>
                                                            </dd>
                                                        </>
                                                    )}
                                                {service.flowDeduction && service.flowDeduction.minutesCovered > 0 && (
                                                    <>
                                                        <dt>
                                                            <Typography
                                                                sx={{ color: 'success.main', fontSize: '0.75rem' }}
                                                            >
                                                                Flow Covered (rated)
                                                            </Typography>
                                                        </dt>
                                                        <dd>
                                                            <Typography
                                                                component="span"
                                                                sx={{ color: 'success.main', fontSize: '0.75rem' }}
                                                            >
                                                                {service.flowDeduction.minutesCovered.toLocaleString()}{' '}
                                                                minutes ({service.flowDeduction.outputsFullyCovered}{' '}
                                                                fully covered outputs,{' '}
                                                                {service.flowDeduction.flowLicensesUsed} flows)
                                                            </Typography>
                                                        </dd>
                                                    </>
                                                )}
                                                <dt>
                                                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                                        Billable
                                                    </Typography>
                                                </dt>
                                                <dd>
                                                    <Typography
                                                        component="span"
                                                        sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                                                    >
                                                        {(service.billableMinutes !== undefined
                                                            ? service.billableMinutes
                                                            : service.minutes
                                                        ).toLocaleString()}{' '}
                                                        minutes
                                                    </Typography>
                                                </dd>
                                                <dt>
                                                    <Typography sx={{ fontSize: '0.75rem' }}>Data</Typography>
                                                </dt>
                                                <dd>
                                                    <Typography component="span" sx={{ fontSize: '0.75rem' }}>
                                                        {bytesToGiB(service.bytes)} GiB
                                                    </Typography>
                                                </dd>
                                            </ServiceGrid>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <AiUsage usage={aiUsage} isLoading={isLoadingAiUsage} error={aiUsageError} />

            {/* Main billing periods table with expandable rows */}
            <Paper sx={{ mt: 2 }}>
                <div style={{ overflowX: 'auto' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox"></TableCell>
                                {columns.map((col) => (
                                    <TableCell key={col.title}>{col.title}</TableCell>
                                ))}
                                <TableCell padding="checkbox"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1} align="center">
                                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                                            <Typography>Loading billing data...</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : billingPeriods.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1} align="center">
                                        <Box sx={{ p: 2 }}>
                                            <Typography>No minute-based billing data found</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                billingPeriods.map((row) => <ExpandableRow key={row.id} row={row} columns={columns} />)
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination could be added here if needed */}
            </Paper>

            {errorBanner}
        </Wrapper>
    )
}

function bytesToGiB(bytes: bigint): string {
    return (Number(bytes / (1024n * 1024n)) / 1024).toFixed(2)
}
